import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContacts } from 'src/store/actions/contacts';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  selectPropertyManagers,
  selectStrataManagers,
} from 'src/store/selectors/contacts';
import { selectPropertyFormData } from 'src/store/selectors/propertyForm';
import {
  setPropertyFormData,
  clearPropertyFormData,
} from 'src/store/actions/propertyForm';
import Button from '../Button';
import Form from '../Form';
import '../SuiteForm/SuiteAreas.scss';
import './PropertyForm.scss';
import Loader from '../Loader';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { clearValidation } from 'src/store/actions/validation';
import { stopFormLoading } from 'src/store/actions/loader';
import * as client from '../../services/client';
import { GOOGLE_API_KEY } from 'src/common/constants';
import { selectProperties } from 'src/store/selectors/properties';
import moment from 'moment';
import {
  fetchPropertyStages,
  fetchPropertyStatuses,
} from 'src/store/actions/formOptions';
import { setSelectedModulePermission } from 'src/store/actions/tabSelections';
import PropertyDetailsForm from 'src/components/PropertyForm/PropertyDetailsForm';

const PropertyForm = ({ onClose, onSaveProperty, tabList }) => {
  const dispatch = useDispatch();

  const [predictions, setPredictions] = useState([]);
  const [location, setLocation] = useState('');
  const [errors, setErrors] = useState<any>({});
  const [sequence, setSequence] = useState({});
  const [disableButton, setDisableButton] = useState(false);

  const propertyFormData = useSelector(selectPropertyFormData);
  const propertyManagers = useSelector(selectPropertyManagers);
  const strataManagers = useSelector(selectStrataManagers);
  const user = useSelector(selectAuthenticatedUser);
  const loader = useSelector(selectFormLoader);
  const properties = useSelector(selectProperties);
  const propertyStages = useSelector(
    (state: any) => state.formOptions.propertyStages
  );
  const propertyStatuses = useSelector(
    (state: any) => state.formOptions.propertyStatuses
  );
  const tabSelections = useSelector((state: any) => state.tabSelections);

  const onFetchContacts = (userId, roleName) =>
    dispatch(fetchContacts(userId, roleName));
  const onSetPropertyFormData = (data) => dispatch(setPropertyFormData(data));
  const onClearPropertyFormData = () => dispatch(clearPropertyFormData());
  const onClearValidation = () => dispatch(clearValidation());
  const onStopFormLoading = () => dispatch(stopFormLoading());
  const onFetchPropertyStages = () => dispatch(fetchPropertyStages());
  const onFetchPropertyStatuses = () => dispatch(fetchPropertyStatuses());
  const onSetSelectedModulePermission = (permissions) =>
    dispatch(setSelectedModulePermission(permissions));

  useEffect(() => {
    onFetchPropertyStages();
    onFetchPropertyStatuses();
    onStopFormLoading();

    if (propertyManagers.length < 1) {
      onFetchContacts(user.id, user.roles[0].role.name);
    }
  }, []);

  const validateAndSave = (buttonAction) => {
    let filteredData;

    if (propertyFormData && propertyFormData.id) {
      filteredData = properties.filter(
        (data) =>
          data.city.toLowerCase().trim() ===
            propertyFormData.city.toLowerCase().trim() &&
          data.name.toLowerCase().trim() ===
            propertyFormData.name.toLowerCase().trim() &&
          propertyFormData.id !== data.id &&
          propertyFormData.propertyManagerId === data.propertyManagerId
      );
    } else {
      filteredData = properties.filter(
        (data) =>
          data.city.toLowerCase().trim() ===
            propertyFormData.city.toLowerCase().trim() &&
          data.name.toLowerCase().trim() ===
            propertyFormData.name.toLowerCase().trim() &&
          propertyFormData.propertyManagerId === data.propertyManagerId
      );
    }

    let constructionStages = [];

    if (propertyFormData.selectedStage && propertyFormData.selectedStatus) {
      constructionStages = [
        ...propertyFormData.constructionStages,
        propertyFormData.selectedStage === 'Custom Stage'
          ? propertyFormData.selectedDate
            ? {
                name: propertyFormData.customStageName,
                type: 'Custom Stage',
                statusId: propertyFormData.selectedStatus,
                date: new Date(
                  moment(propertyFormData.selectedDate).format('MM/DD/YYYY')
                ),
              }
            : {
                name: propertyFormData.customStageName,
                type: 'Custom Stage',
                statusId: propertyFormData.selectedStatus,
              }
          : propertyFormData.selectedDate
            ? {
                id: propertyFormData.selectedStage,
                statusId: propertyFormData.selectedStatus,
                date: new Date(
                  moment(propertyFormData.selectedDate).format('MM/DD/YYYY')
                ),
              }
            : {
                id: propertyFormData.selectedStage,
                statusId: propertyFormData.selectedStatus,
              },
      ];
    } else {
      constructionStages = propertyFormData.constructionStages;
    }

    let newErrors: any = {};

    if (propertyFormData.name.trim().length === 0) {
      newErrors.errors = true;
      newErrors.name = 'Please Enter Property Name !';
    }
    if (propertyFormData.addressLine1.trim().length === 0) {
      newErrors.errors = true;
      newErrors.addressLine1 = 'Please Enter Address !';
    }
    if (propertyFormData.city.trim().length === 0) {
      newErrors.errors = true;
      newErrors.city = 'Please Enter City !';
    }
    if (propertyFormData.province.trim().length === 0) {
      newErrors.errors = true;
      newErrors.province = 'Please Enter Province !';
    }
    if (propertyFormData.postalCode.trim().length === 0) {
      newErrors.errors = true;
      newErrors.postalCode = 'Please Enter Postal Code !';
    }
    if (propertyFormData.postalCode.trim().length < 5) {
      newErrors.errors = true;
      newErrors.postalCode =
        'Please enter a valid postal code with min 5 characters !';
    }
    if (propertyFormData.postalCode.trim().length > 10) {
      newErrors.errors = true;
      newErrors.postalCode =
        'Please enter a valid postal code with max 10 characters !';
    }

    let newPropertyCondition;

    if (filteredData && filteredData.length > 0) {
      newPropertyCondition = true;
      alert(
        'You have already a property with same name and location to that Property Developer.'
      );
    } else {
      newPropertyCondition = false;
    }

    setErrors(newErrors);

    if (!newErrors.errors && !newPropertyCondition) {
      setDisableButton(true);

      const saveData = {
        ...propertyFormData,
        constructionStages: constructionStages,
      };

      onSaveProperty(saveData, buttonAction);

      if (buttonAction === 'SaveAndClose') {
        onClose();
        onClearPropertyFormData();
        onClearValidation();
      } else if (buttonAction === 'SaveAndAdd') {
        onClearPropertyFormData();
        onClearValidation();
      }
    }
  };

  const onErrorEmpty = (value) => {
    let newErrors = { ...errors };
    newErrors[value] = '';
    setErrors(newErrors);
  };

  const getLocation = async (value) => {
    setLocation(value);
    client.getLocations(value).then((response: any) => {
      if (response.status === 200) {
        response.json().then((responseJson) => {
          let data = JSON.parse(responseJson);
          setPredictions(data.predictions);
        });
      } else {
        setPredictions([]);
      }
    });
  };

  const onClickLocation = async (address) => {
    const apiurl = `https://maps.google.com/maps/api/geocode/json?key=${GOOGLE_API_KEY}&address=${address}`;

    try {
      const result = await fetch(apiurl);
      const json = await result.json();
      let latitude = json.results[0].geometry.location.lat;
      let longitude = json.results[0].geometry.location.lng;
      const apiurl1 = `https://maps.google.com/maps/api/geocode/json?key=${GOOGLE_API_KEY}&latlng=${latitude},${longitude}`;

      const getResult = await fetch(apiurl1);
      const addressObj = await getResult.json();
      let addressLine1;
      let city;
      let state;
      let pincode;
      let addressData = addressObj.results[0].address_components;

      if (addressData && addressData.length > 0) {
        addressData.forEach((data) => {
          if (data.types.indexOf('street_number') != -1) {
            addressLine1 = data.long_name;
          }
          if (data.types.indexOf('route') != -1) {
            addressLine1 = addressLine1
              ? addressLine1 + ', ' + data.long_name
              : data.long_name;
          }
          if (data.types.indexOf('neighborhood') != -1) {
            addressLine1 = addressLine1
              ? addressLine1 + ', ' + data.long_name
              : data.long_name;
          }
          if (data.types.indexOf('sublocality_level_2') != -1) {
            addressLine1 = addressLine1
              ? addressLine1 + ', ' + data.long_name
              : data.long_name;
          }
          if (data.types.indexOf('sublocality_level_1') != -1) {
            addressLine1 = addressLine1
              ? addressLine1 + ', ' + data.long_name
              : data.long_name;
          }
          if (data.types.indexOf('locality') != -1) {
            city = data.long_name;
          }
          if (data.types.indexOf('administrative_area_level_1') != -1) {
            state = data.long_name;
          }
          if (data.types.indexOf('postal_code') != -1) {
            pincode = data.long_name;
          }
        });

        onSetPropertyFormData({
          addressLine1: addressLine1,
          city: city,
          province: state,
          postalCode: pincode,
        });
        setPredictions([]);
        setLocation('');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onAddNewConstructionStatus = (type) => {
    if (type === false) {
      onSetPropertyFormData({
        constructionStages:
          propertyFormData && propertyFormData.id
            ? [...propertyFormData.constructionStages, { new: true }]
            : [{ new: true }],
      });
      onSetPropertyFormData({
        selectedStage: '',
        selectedStatus: '',
        selectedDate: '',
        customStageName: '',
        isCustomStageEnabled: false,
      });
    } else {
      if (
        propertyFormData &&
        propertyFormData.constructionStages &&
        propertyFormData.constructionStages.length > 0
      ) {
        let constructionStages = propertyFormData.constructionStages;
        constructionStages = propertyFormData.constructionStages.filter(
          (cs) => !cs.new
        );

        onSetPropertyFormData({
          constructionStages: [
            ...constructionStages,
            propertyFormData.selectedStage === 'Custom Stage'
              ? propertyFormData.selectedDate
                ? {
                    name: propertyFormData.customStageName,
                    type: 'Custom Stage',
                    statusId: propertyFormData.selectedStatus,
                    date: new Date(
                      moment(propertyFormData.selectedDate).format('MM/DD/YYYY')
                    ),
                  }
                : {
                    name: propertyFormData.customStageName,
                    type: 'Custom Stage',
                    statusId: propertyFormData.selectedStatus,
                  }
              : propertyFormData.selectedDate
                ? {
                    id: propertyFormData.selectedStage,
                    statusId: propertyFormData.selectedStatus,
                    date: new Date(
                      moment(propertyFormData.selectedDate).format('MM/DD/YYYY')
                    ),
                  }
                : {
                    id: propertyFormData.selectedStage,
                    statusId: propertyFormData.selectedStatus,
                  },
            {
              new: true,
            },
          ],
        });
      } else {
        onSetPropertyFormData({
          constructionStages: [
            propertyFormData.selectedStage === 'Custom Stage'
              ? propertyFormData.selectedDate
                ? {
                    name: propertyFormData.customStageName,
                    type: 'Custom Stage',
                    statusId: propertyFormData.selectedStatus,
                    date: new Date(
                      moment(propertyFormData.selectedDate).format('MM/DD/YYYY')
                    ),
                  }
                : {
                    name: propertyFormData.customStageName,
                    type: 'Custom Stage',
                    statusId: propertyFormData.selectedStatus,
                  }
              : propertyFormData.selectedDate
                ? {
                    id: propertyFormData.selectedStage,
                    statusId: propertyFormData.selectedStatus,
                    date: new Date(
                      moment(propertyFormData.selectedDate).format('MM/DD/YYYY')
                    ),
                  }
                : {
                    id: propertyFormData.selectedStage,
                    statusId: propertyFormData.selectedStatus,
                  },
          ],
        });
      }

      onSetPropertyFormData({
        selectedStage: '',
        selectedStatus: '',
        selectedDate: '',
        customStageName: '',
        isCustomStageEnabled: false,
      });
    }
  };

  const onhandleStatusUpdate = (value, index, type) => {
    let data = propertyFormData.constructionStages.map((ps, index1) => {
      if (index === index1) {
        if (ps.type === 'Custom Stage' && ps.id && ps.id !== value) {
          if (type === 'name') {
            return { ...ps, name: value };
          } else {
            return { name: '', type: null, statusId: ps.statusId, id: value };
          }
        } else if (value === 'Custom Stage') {
          return { name: '', type: 'Custom Stage', statusId: ps.statusId };
        } else {
          if (type === 'name') {
            return { ...ps, name: value, type: 'Custom Stage' };
          } else {
            return {
              ...ps,
              [type]:
                type === 'date'
                  ? new Date(moment(value).format('MM/DD/YYYY'))
                  : value,
            };
          }
        }
      } else {
        return ps;
      }
    });
    onSetPropertyFormData({ constructionStages: data });
  };

  const onhandleStatusDelete = (index, obj) => {
    let data = propertyFormData.constructionStages.filter((ps) => ps !== obj);
    onSetPropertyFormData({
      constructionStages: data,
    });
  };

  const onChangeRank = (data, index) => {
    let sortArray = propertyFormData.constructionStages.map((ps) => {
      if (ps === sequence) {
        return data;
      } else if (ps === data) {
        return sequence;
      } else {
        return ps;
      }
    });
    onSetPropertyFormData({
      constructionStages: sortArray,
    });
  };

  const onSetModules = (selectedModule) => {
    let selectedModulePermissions = tabSelections.selectedModulePermissions;
    let index = selectedModulePermissions.findIndex(
      (object) => object.Id === selectedModule
    );

    if (index === -1) {
      onSetSelectedModulePermission([
        ...tabSelections.selectedModulePermissions,
        { Id: selectedModule },
      ]);
      onSetPropertyFormData({
        modulePermissions: [
          ...tabSelections.selectedModulePermissions,
          { Id: selectedModule },
        ],
      });
    } else {
      selectedModulePermissions = selectedModulePermissions.filter(
        (data) => data.Id !== selectedModule
      );
      onSetSelectedModulePermission(selectedModulePermissions);
      onSetPropertyFormData({ modulePermissions: selectedModulePermissions });
    }
  };

  let filteredPropertyManagers = propertyManagers;
  let isModulePErmissionsSelected = propertyFormData.hasOwnProperty(
    'modulePermissions'
  )
    ? propertyFormData.modulePermissions.length > 0
    : false;
  let selectedModulePermissions = tabSelections.selectedModulePermissions;

  if (user.roles[0].role.name === 'Property Developer') {
    filteredPropertyManagers = propertyManagers.filter(
      (item) => item.id === user.id
    );
  }

  let filterData =
    propertyFormData &&
    propertyFormData.constructionStages &&
    propertyFormData.constructionStages.length > 0
      ? propertyFormData.constructionStages.filter((cs) => cs.new)
      : [];

  return (
    <Form
      headline={(propertyFormData.id ? 'Edit' : 'Add a') + ' Property'}
      onClose={onClose}
      actions={
        <>
          {loader ? <Loader center={true} /> : null}
          {!propertyFormData.id ? (
            <Button
              color="semantic"
              size="small"
              label="Save and Add Another"
              onClick={() => validateAndSave('SaveAndAdd')}
              disabled={
                !propertyFormData.name ||
                !propertyFormData.addressLine1 ||
                !propertyFormData.city ||
                !propertyFormData.province ||
                !propertyFormData.postalCode ||
                !propertyFormData.propertyManagerId ||
                !isModulePErmissionsSelected ||
                disableButton
              }
            />
          ) : (
            <div />
          )}
          <Button
            color="teal"
            size="small"
            label="Save Property"
            onClick={() => validateAndSave('SaveAndClose')}
            disabled={
              !propertyFormData.name ||
              !propertyFormData.addressLine1 ||
              !propertyFormData.city ||
              !propertyFormData.province ||
              !propertyFormData.postalCode ||
              !isModulePErmissionsSelected ||
              disableButton ||
              (propertyFormData.propertyManager &&
              propertyFormData.propertyManager.id
                ? !propertyFormData.propertyManager.id
                : !propertyFormData.propertyManagerId ||
                  propertyFormData.propertyManagerId ===
                    '00000000-0000-0000-0000-000000000000')
            }
          />
        </>
      }
      component={
        <PropertyDetailsForm
          propertyFormData={propertyFormData}
          onSetPropertyFormData={onSetPropertyFormData}
          errors={errors}
          onErrorEmpty={onErrorEmpty}
          location={location}
          getLocation={getLocation}
          predictions={predictions}
          onClickLocation={onClickLocation}
          filteredPropertyManagers={filteredPropertyManagers}
          strataManagers={strataManagers}
          tabList={tabList}
          selectedModulePermissions={selectedModulePermissions}
          onSetModules={onSetModules}
          propertyStages={propertyStages}
          propertyStatuses={propertyStatuses}
          onhandleStatusUpdate={onhandleStatusUpdate}
          onhandleStatusDelete={onhandleStatusDelete}
          setSequence={setSequence}
          onChangeRank={onChangeRank}
          filterData={filterData}
          onAddNewConstructionStatus={onAddNewConstructionStatus}
        />
      }
      footer={undefined}
      onSubmit={undefined}
      strapline={undefined}
      onExit={undefined}
    />
  );
};

export default PropertyForm;
